import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faScaleBalanced,
    faGavel,
    faHandshake,
    faUsers,
    faChartLine,
    faShieldAlt,
    faLightbulb,
    faBuilding,
    faBook,
    faAward,
    faClock,
    faCheckCircle,
    faChevronRight,
    faStar,
    faUniversity,
    faLandmark,
    faScaleUnbalanced,
    faMoneyBillWave,
    faUserTie
} from "@fortawesome/free-solid-svg-icons";
import { Cta } from "../components/Cta";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};

const litigationAreas = [
    {
        icon: faScaleBalanced,
        title: "Civil Litigation",
        description: "Comprehensive representation in civil disputes including property, contracts, torts, and recovery suits.",
        cases: ["Property Disputes", "Contract Breaches", "Tort Claims", "Recovery Suits"],
        color: "gold"
    },
    {
        icon: faGavel,
        title: "Criminal Litigation",
        description: "Defense and prosecution in criminal matters including anticipatory bails, regular bails, and quashing petitions.",
        cases: ["Anticipatory Bail", "Regular Bail", "Quashing Petitions", "White-collar Crimes"],
        color: "primary-blue"
    },
    {
        icon: faBuilding,
        title: "Corporate Litigation",
        description: "Handling corporate disputes, shareholder conflicts, company law matters, and commercial litigation.",
        cases: ["Shareholder Disputes", "NCLT Matters", "Commercial Contracts", "Corporate Governance"],
        color: "gold"
    },
    {
        icon: faMoneyBillWave,
        title: "Banking & Finance Litigation",
        description: "Representation in banking disputes, loan recovery, securitization matters, and financial litigation.",
        cases: ["Loan Recovery", "DRT Matters", "Securitization", "Banking Frauds"],
        color: "primary-blue"
    },
    {
        icon: faBook,
        title: "Tax Litigation",
        description: "Representation before tax tribunals and courts for direct and indirect taxation matters.",
        cases: ["Income Tax Appeals", "GST Disputes", ],
        color: "gold"
    },
    {
        icon: faUsers,
        title: "Service & Labour Litigation",
        description: "Employment disputes, service matters, labour court cases, and industrial disputes.",
        cases: ["Service Matters", "Labour Court", "Industrial Disputes", "Employment Contracts"],
        color: "primary-blue"
    },
    {
        icon: faShieldAlt,
        title: "Consumer Protection",
        description: "Representation before consumer forums for defective goods, deficient services, and unfair trade practices.",
        cases: ["Defective Products", "Deficient Services", "Unfair Trade", "Medical Negligence"],
        color: "gold"
    },
    {
        icon: faLandmark,
        title: "Constitutional Matters",
        description: "Writ petitions, public interest litigation, and constitutional challenges before High Courts and Supreme Court.",
        cases: ["Writ Petitions", "PILs", "Constitutional Challenges", "Fundamental Rights"],
        color: "primary-blue"
    }
];

const disputeResolutionMethods = [
    {
        method: "Litigation",
        icon: faScaleUnbalanced,
        description: "Traditional court-based dispute resolution through judicial process.",
        courts: ["Supreme Court", "High Courts", "District Courts", "Special Tribunals"],
        bestFor: ["Complex Legal Issues", "Precedent Setting", "Enforcement Needed"],
        color: "gold"
    },
    {
        method: "Arbitration",
        icon: faHandshake,
        description: "Private dispute resolution through arbitrators, faster and more flexible than court litigation.",
        courts: ["Domestic Arbitration", "International Arbitration", "Institutional Arbitration"],
        bestFor: ["Commercial Disputes", "Confidential Matters", "Technical Expertise Needed"],
        color: "primary-blue"
    },
    {
        method: "Mediation",
        icon: faUsers,
        description: "Facilitated negotiation with a neutral mediator helping parties reach mutual agreement.",
        courts: ["Court-annexed Mediation", "Private Mediation", "Online Mediation"],
        bestFor: ["Preserving Relationships", "Creative Solutions", "Cost-effective Resolution"],
        color: "gold"
    },
    {
        method: "Conciliation",
        icon: faLightbulb,
        description: "Similar to mediation but conciliator plays more active role in suggesting solutions.",
        courts: ["Statutory Conciliation", "Voluntary Conciliation", "Industrial Conciliation"],
        bestFor: ["Labor Disputes", "Family Matters", "Community Disputes"],
        color: "primary-blue"
    }
];

const courtsTribunals = [
    {
        name: "Supreme Court of India",
        level: "Apex Court",
        icon: faUniversity,
        matters: ["Constitutional Matters", "Special Leave Petitions", "Appeals"]
    },
    {
        name: "Patna High Court",
        level: "High Court",
        icon: faLandmark,
        matters: ["Writ Jurisdiction", "Appellate Matters", "Original Jurisdiction","Criminal Matters"]
    },
    // {
    //     name: "Jharkhand High Court",
    //     level: "High Court",
    //     icon: faLandmark,
    //     matters: ["Writ Jurisdiction", "Appellate Matters", "Original Jurisdiction"]
    // },
    {
        name: "District Courts",
        level: "District Level",
        icon: faScaleBalanced,
        matters: ["Original Suits", "Civil Appeals", "Criminal Cases"]
    },
    // {
    //     name: "National Company Law Tribunal",
    //     level: "Special Tribunal",
    //     icon: faBuilding,
    //     matters: ["Company Law", "Insolvency", "Corporate Restructuring"]
    // },
    {
        name: "Debt Recovery Tribunal",
        level: "Special Tribunal",
        icon: faMoneyBillWave,
        matters: ["Bank Loan Recovery", "Securitization", "Banking Disputes"]
    },
    {
        name: "Consumer Forums",
        level: "Consumer Courts",
        icon: faShieldAlt,
        matters: ["Consumer Complaints", "Deficient Services", "Unfair Trade"]
    },
    // {
    //     name: "Tax Tribunals",
    //     level: "Special Tribunal",
    //     icon: faBook,
    //     matters: ["Income Tax Appeals", "GST Disputes", "Customs Appeals"]
    // }
];

const successStories = [
    {
        case: "₹500 Cr Infrastructure Contract Dispute",
        client: "Tata Projects Ltd",
        result: "Successful settlement through arbitration",
        duration: "6 months",
        icon: faBuilding
    },
    {
        case: "High-Profile Criminal Bail Matter",
        client: "Corporate Executive",
        result: "Anticipatory bail granted",
        duration: "2 weeks",
        icon: faGavel
    },
    {
        case: "Complex Property Dispute",
        client: "NRIs based in USA",
        result: "Favorable High Court judgment",
        duration: "18 months",
        icon: faScaleBalanced
    },
    {
        case: "Banking Fraud Recovery",
        client: "HDFC Bank Ltd",
        result: "₹25 Cr recovered through DRT",
        duration: "8 months",
        icon: faMoneyBillWave
    }
];

export const DisputeResolution = () => {
    const [activeMethod, setActiveMethod] = useState(0);
    const [activeArea, setActiveArea] = useState(0);

    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                <div className="position-absolute top-0 end-0 w-25 h-25"
                    style={{
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(30%, -30%)"
                    }}
                ></div>
                <div className="position-absolute bottom-0 start-0 w-30 h-30"
                    style={{
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        transform: "translate(-30%, 30%)"
                    }}
                ></div>
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span className="d-inline-block px-4 py-2 rounded-pill mb-3"
                            style={{
                                color: "var(--gold)",
                                fontWeight: "600",
                                letterSpacing: "2px",
                                fontSize: "0.9rem",
                                textTransform: "uppercase",
                                border: "1px solid var(--gold-light)",
                                background: "rgba(184, 134, 11, 0.05)",
                                backdropFilter: "blur(4px)"
                            }}
                        >
                            Legal Expertise
                        </span>

                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            Dispute Resolution & Litigation Services
                        </motion.h4>
                    </motion.div>

                    <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        RNA Law Chambers provides comprehensive dispute resolution services through litigation,
                        arbitration, mediation, and conciliation. Our  Advocates-on-Record
                        represent clients across several courts and tribunals in India.
                    </motion.p>
                </motion.div>

                {/* Litigation Areas */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faScaleBalanced} className="me-3" style={{ color: "var(--gold)" }} />
                            Areas of Litigation Expertise
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Comprehensive representation across diverse legal domains
                        </p>
                    </div>

                    <div className="row g-4">
                        {litigationAreas.map((area, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveArea(index)}
                            >
                                <motion.div
                                    className="h-100 p-4 rounded-4 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${area.color}-light)`,
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: `var(--${area.color})`
                                    }}
                                >
                                    <div className="position-relative z-1 h-100 d-flex flex-column">
                                        {/* Icon */}
                                        <motion.div
                                            className="mb-4"
                                            whileHover={{ rotate: 360 }}
                                            transition={{ duration: 0.6, ease: "easeInOut" }}
                                        >
                                            <div className="p-3 rounded-3 d-inline-block"
                                                style={{
                                                    backgroundColor: `rgba(var(--${area.color}-rgb), 0.1)`,
                                                    color: `var(--${area.color})`
                                                }}
                                            >
                                                <FontAwesomeIcon icon={area.icon} style={{ fontSize: "1.5rem" }} />
                                            </div>
                                        </motion.div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {area.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1 small"
                                            style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {area.description}
                                        </p>

                                        {/* Cases */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${area.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {area.cases.map((caseType, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            className="flex-shrink-0"
                                                            style={{
                                                                color: `var(--${area.color})`,
                                                                fontSize: "0.8rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {caseType}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Dispute Resolution Methods */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faHandshake} className="me-3" style={{ color: "var(--gold)" }} />
                            Dispute Resolution Methods
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Tailored approaches for different types of legal disputes
                        </p>
                    </div>

                    {/* Method Selection */}
                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                        {disputeResolutionMethods.map((method, index) => (
                            <motion.button
                                key={index}
                                className={`btn px-4 py-3 rounded-pill d-flex align-items-center gap-3 ${activeMethod === index ? 'active' : ''}`}
                                onClick={() => setActiveMethod(index)}
                                variants={itemVariants}
                                style={{
                                    backgroundColor: activeMethod === index ? `var(--${method.color})` : "transparent",
                                    color: activeMethod === index ? "var(--white)" : `var(--${method.color})`,
                                    border: `2px solid var(--${method.color})`,
                                    fontWeight: "600",
                                    transition: "var(--transition)",
                                    minWidth: "200px"
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    backgroundColor: activeMethod === index ? `var(--${method.color}-light)` : `rgba(var(--${method.color}-rgb), 0.1)`
                                }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <FontAwesomeIcon icon={method.icon} />
                                <span>{method.method}</span>
                            </motion.button>
                        ))}
                    </div>

                    {/* Method Details */}
                    <motion.div
                        className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--white)",
                            border: `2px solid var(--${disputeResolutionMethods[activeMethod].color})`,
                            boxShadow: "var(--shadow-medium)"
                        }}
                        key={activeMethod}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="row g-4">
                            <div className="col-lg-8">
                                <div className="d-flex align-items-center gap-3 mb-4">
                                    <div className="p-3 rounded-3"
                                        style={{
                                            backgroundColor: `rgba(var(--${disputeResolutionMethods[activeMethod].color}-rgb), 0.1)`,
                                            color: `var(--${disputeResolutionMethods[activeMethod].color})`
                                        }}
                                    >
                                        <FontAwesomeIcon icon={disputeResolutionMethods[activeMethod].icon} style={{ fontSize: "2rem" }} />
                                    </div>
                                    <div>
                                        <h3 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                            {disputeResolutionMethods[activeMethod].method}
                                        </h3>
                                        <p className="mb-0" style={{ color: "var(--text-primary)" }}>
                                            {disputeResolutionMethods[activeMethod].description}
                                        </p>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <h6 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            Available Forums
                                        </h6>
                                        <div className="vstack gap-2">
                                            {disputeResolutionMethods[activeMethod].courts.map((court, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faLandmark}
                                                        style={{
                                                            color: "var(--gold)",
                                                            fontSize: "0.8rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {court}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <h6 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            Best Suited For
                                        </h6>
                                        <div className="vstack gap-2">
                                            {disputeResolutionMethods[activeMethod].bestFor.map((suit, idx) => (
                                                <div key={idx} className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon
                                                        icon={faCheckCircle}
                                                        style={{
                                                            color: "var(--green-accent)",
                                                            fontSize: "0.8rem"
                                                        }}
                                                    />
                                                    <span className="small" style={{ color: "var(--text-primary)" }}>
                                                        {suit}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: `rgba(var(--${disputeResolutionMethods[activeMethod].color}-rgb), 0.05)`,
                                        border: `1px solid var(--${disputeResolutionMethods[activeMethod].color}-light)`
                                    }}
                                >
                                    <h6 className="fw-bold mb-3" style={{ color: `var(--${disputeResolutionMethods[activeMethod].color})` }}>
                                        Key Advantages
                                    </h6>
                                    <div className="vstack gap-3">
                                        {[
                                            "Expert Legal Representation",
                                            "Strategic Case Management",
                                            "Regular Client Updates",
                                            "Cost-effective Solutions"
                                        ].map((advantage, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faStar}
                                                    style={{
                                                        color: `var(--${disputeResolutionMethods[activeMethod].color})`,
                                                        fontSize: "0.8rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {advantage}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* Courts & Tribunals */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faUniversity} className="me-3" style={{ color: "var(--gold)" }} />
                            Courts & Tribunals We Practice In
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Extensive experience across judicial forums in India
                        </p>
                    </div>

                    <div className="row g-4 d-flex justify-content-center">
                        {courtsTribunals.map((court, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: index < 3 ? "2px solid var(--gold)" : "1px solid var(--gold)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)"
                                    }}
                                >
                                    <div className="text-center mb-3">
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={court.icon} style={{ fontSize: "1.5rem" }} />
                                        </div>
                                    </div>

                                    <h6 className="fw-bold text-center mb-2" style={{
                                        color: index < 3 ? "var(--gold)" : "var(--gold)"
                                    }}>
                                        {court.name}
                                    </h6>

                                    <div className="text-center mb-3">
                                        <span className="badge px-3 py-1"
                                            style={{
                                                backgroundColor: index < 3 ? "rgba(var(--gold-rgb), 0.1)" : "rgba(var(--primary-blue-rgb), 0.1)",
                                                color: index < 3 ? "var(--gold)" : "var(--gold)",
                                                fontSize: "0.75rem"
                                            }}
                                        >
                                            {court.level}
                                        </span>
                                    </div>

                                    <div className="vstack gap-2">
                                        {court.matters.map((matter, idx) => (
                                            <div key={idx} className="d-flex align-items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={faChevronRight}
                                                    style={{
                                                        color: "var(--gold)",
                                                        fontSize: "0.7rem"
                                                    }}
                                                />
                                                <span className="small" style={{ color: "var(--text-primary)" }}>
                                                    {matter}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Success Stories */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    {/* <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faAward} className="me-3" style={{ color: "var(--gold)" }} />
                            Recent Success Stories
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            Demonstrated expertise in complex dispute resolution
                        </p>
                    </div>

                    <div className="row g-4">
                        {successStories.map((story, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)",
                                        transition: "var(--transition)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "var(--shadow-medium)",
                                        borderColor: "var(--gold)"
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        <div className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)"
                                            }}
                                        >
                                            <FontAwesomeIcon icon={story.icon} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                {story.case}
                                            </h6>
                                            <div className="small" style={{ color: "var(--gold)" }}>
                                                {story.client}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <FontAwesomeIcon
                                                icon={faCheckCircle}
                                                style={{
                                                    color: "var(--green-accent)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small fw-bold" style={{ color: "var(--text-primary)" }}>
                                                Result: {story.result}
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faClock}
                                                style={{
                                                    color: "var(--primary-blue)",
                                                    fontSize: "0.8rem"
                                                }}
                                            />
                                            <span className="small" style={{ color: "var(--text-primary)" }}>
                                                Duration: {story.duration}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-auto pt-3 border-top"
                                        style={{ borderColor: "var(--gold-light)" }}
                                    >
                                        <motion.button
                                            className="btn btn-sm w-100"
                                            style={{
                                                backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                color: "var(--gold)",
                                                border: "1px solid var(--gold-light)"
                                            }}
                                            whileHover={{
                                                backgroundColor: "var(--gold)",
                                                color: "var(--white)"
                                            }}
                                        >
                                            View Case Details
                                        </motion.button>
                                    </div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div> */}
                </motion.div>

                {/* Our Approach */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        <FontAwesomeIcon icon={faChartLine} className="me-3" style={{ color: "var(--gold)" }} />
                                        Strategic Litigation Approach
                                    </h3>
                                    <div className="row g-4 mb-4">
                                        {[
                                            { label: "In Depth Case Analysis", value: "100%", icon: faLightbulb },
                                            { label: "Good Success Rate", value: "96%", icon: faChartLine },
                                            { label: "Focus Client Satisfaction", value: "98%", icon: faUsers },
                                            { label: "Timely Updates", value: "24/7", icon: faClock }
                                        ].map((stat, index) => (
                                            <motion.div
                                                key={index}
                                                className="col-6 col-md-3"
                                                variants={itemVariants}
                                            >
                                                <div className="text-center">
                                                    <div className="mb-2">
                                                        <FontAwesomeIcon
                                                            icon={stat.icon}
                                                            style={{
                                                                color: "var(--gold)",
                                                                fontSize: "1.5rem"
                                                            }}
                                                        />
                                                    </div>
                                                    {/* <div className="display-6 fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        {stat.value}
                                                    </div> */}
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <p className="mb-0" style={{
                                        color: "rgba(255, 255, 255, 0.9)",
                                        lineHeight: 1.7
                                    }}>
                                        Our systematic approach includes thorough case analysis, strategic planning,
                                        diligent execution, and regular client communication to ensure the best
                                        possible outcomes in dispute resolution.
                                    </p>
                                </motion.div>
                            </div>

                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faUserTie}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        <div className="fw-bold mb-2" style={{ color: "var(--white)", fontSize: "1.2rem" }}>
                                            Advocate-on-Record
                                        </div>
                                        <div style={{ color: "var(--gold)" }}>
                                            Patna High Court Practice
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
                <Cta
                    Title="Need Expert Dispute Resolution?"
                    description="Contact our Advocates-on-Record for a confidential consultation on your dispute resolution needs."
                />



            </div>
        </div>
    );
};